import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTractor } from '@fortawesome/free-solid-svg-icons';
import Circles from '../../../assets/images/circle.png';
import axios from 'axios';

const KVKDashboard = ({ 
  loading, 
  handleMoreInfoClick 
}) => {
  // Get codes from session storage
  let atariCode = sessionStorage.getItem("atariCode");
  atariCode = parseInt(atariCode);
  let kvkCode = sessionStorage.getItem("kvkCode") || sessionStorage.getItem("kvkCodeModel");
  kvkCode = parseInt(kvkCode);
  
  // Local state for KVK Admin specific data
  const [userCount, setUserCount] = useState(0);
  const [demonstrationCount, setDemonstrationCount] = useState(0);
  const [localLoading, setLocalLoading] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [demonstrationDetails, setDemonstrationDetails] = useState([]);
  const [activeTable, setActiveTable] = useState('none'); // 'none', 'user', 'demonstration'
  const [drillDownData, setDrillDownData] = useState([]);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [selectedUserCode, setSelectedUserCode] = useState(null);
  const [demoDrillDownData, setDemoDrillDownData] = useState([]);
  const [showDemoDrillDown, setShowDemoDrillDown] = useState(false);
  const [selectedDemoIndex, setSelectedDemoIndex] = useState(null);

  // Helper function for CSV export
  const exportToCSV = (data, filename, headers) => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    const csvRows = [headers.join(",")];

    data.forEach((item, index) => {
      const row = [
        index + 1,
        `"${item.name || item.stateName || item.districtName || item.userName || item.userRoleName || 'N/A'}"`,
        item.count || item.total || item.totalUsers || 0
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchUserDetails();
    fetchDemonstrationCount();
  }, []);

  // Fetch KVK Dashboard Report Count
  const fetchKVKDashboardData = async () => {
    try {
      setLocalLoading(true);
      console.log('=== KVK Dashboard - Fetching Data ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR`,
        {
          "flag": "KVK-1",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('KVK Dashboard API Response:', data);

      if (data.success && data.dataContent && data.dataContent.length > 0) {
        // Calculate total users from all user types
        const totalUsers = data.dataContent.reduce((sum, userType) => {
          return sum + parseInt(userType.totalUsers || 0);
        }, 0);
        
        setUserCount(totalUsers);
        console.log('KVK Total Users set to:', totalUsers);
      } else {
        setUserCount(0);
        console.log('KVK Dashboard API returned no data');
      }
    } catch (error) {
      console.error('Error fetching KVK Dashboard data:', error);
      setUserCount(0);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch user details for More Info
  const fetchUserDetails = async () => {
    try {
      setLocalLoading(true);
      console.log('=== KVK Dashboard - Fetching User Details ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR`,
        {
          "flag": "KVK-2",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('KVK User Details API Response:', data);

      if (data.success && data.dataContent) {
        setUserDetails(Array.isArray(data.dataContent) ? data.dataContent : []);
      } else {
        setUserDetails([]);
      }
    } catch (error) {
      console.error('Error fetching KVK user details:', error);
      setUserDetails([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch demonstration count using ICAR_SurveyCount API with KVK_L flag
  const fetchDemonstrationCount = async () => {
    try {
      setLocalLoading(true);
      console.log('=== KVK Dashboard - Fetching Demonstration Count ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR_SurveyCount`,
        {
          "flag": "KVK_L",
          "KvkCode": parseInt(kvkCode, 10)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('KVK Demonstration Count API Response:', data);

      if (data.success && data.dataContent && data.dataContent.length > 0) {
        const totalCount = data.dataContent.reduce((sum, item) => {
          return sum + parseInt(item.total || item.count || 0, 10);
        }, 0);
        setDemonstrationCount(totalCount);
        console.log('KVK Demonstration Count set to:', totalCount);
      } else {
        setDemonstrationCount(0);
        console.log('KVK Demonstration Count API returned no data');
      }
    } catch (error) {
      console.error('Error fetching KVK demonstration count:', error);
      setDemonstrationCount(0);
    } finally {
      setLocalLoading(false);
    }
  };

  // Fetch demonstration details for More Info using KVK_L1 flag
  const fetchDemonstrationDetails = async () => {
    try {
      setLocalLoading(true);
      console.log('=== KVK Dashboard - Fetching Demonstration Details ===');
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR_SurveyCount`,
        {
          "flag": "KVK_L1",
          "KvkCode": parseInt(kvkCode, 10)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('KVK Demonstration Details API Response:', data);

      if (data.success && data.dataContent) {
        setDemonstrationDetails(Array.isArray(data.dataContent) ? data.dataContent : []);
      } else {
        setDemonstrationDetails([]);
      }
    } catch (error) {
      console.error('Error fetching KVK demonstration details:', error);
      setDemonstrationDetails([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Handle More Info click
  const handleUserMoreInfo = async () => {
    if (activeTable === 'user') {
      setActiveTable('none');
      setUserDetails([]);
      setShowDrillDown(false);
      setDrillDownData([]);
      setSelectedUserCode(null);
    } else {
      setActiveTable('user');
      // Close demonstration table and its drill-down if open
      setDemonstrationDetails([]);
      setShowDemoDrillDown(false);
      setDemoDrillDownData([]);
      await fetchUserDetails();
      // Scroll to table
      setTimeout(() => {
        const tableElement = document.getElementById('kvk-user-details-table');
        if (tableElement) {
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleDemonstrationMoreInfo = async () => {
    if (activeTable === 'demonstration') {
      setActiveTable('none');
      setDemonstrationDetails([]);
      setShowDemoDrillDown(false);
      setDemoDrillDownData([]);
    } else {
      setActiveTable('demonstration');
      // Close user table and its drill-down if open
      setUserDetails([]);
      setShowDrillDown(false);
      setDrillDownData([]);
      setSelectedUserCode(null);
      await fetchDemonstrationDetails();
      // Scroll to table
      setTimeout(() => {
        const tableElement = document.getElementById('kvk-demonstration-details-table');
        if (tableElement) {
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Toggle drill-down data for User details
  const toggleDrillDownData = async (kvkName, kvkCode) => {
    // If the same KVK is clicked, toggle it closed
    if (showDrillDown && selectedUserCode === kvkCode) {
      setShowDrillDown(false);
      setDrillDownData([]);
      setSelectedUserCode(null);
      return;
    }

    // Otherwise, fetch and show the drill-down data
    try {
      setLocalLoading(true);
      console.log('=== KVK Dashboard - Fetching Drill Down Data ===');
      console.log('Selected KVK Code:', kvkCode);
      
      const { data } = await axios.post(
        `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/ICAR`,
        {
          "flag": "KVK-3",
          "atariCode": parseInt(atariCode)
        },
        {
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('KVK Drill Down API Response:', data);

      if (data.success && data.dataContent) {
        // Filter data for the selected KVK
        const filteredData = data.dataContent.filter(item => item.kvkCode === kvkCode);
        console.log('Filtered Data for KVK Code', kvkCode, ':', filteredData);
        setDrillDownData(filteredData);
        setSelectedUserCode(kvkCode);
        setShowDrillDown(true);
        
        // Scroll to drill-down table
        setTimeout(() => {
          const tableElement = document.getElementById('kvk-drilldown-table');
          if (tableElement) {
            tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        setDrillDownData([]);
      }
    } catch (error) {
      console.error('Error fetching KVK drill-down data:', error);
      setDrillDownData([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Close drill-down table
  const closeDrillDown = () => {
    setShowDrillDown(false);
    setDrillDownData([]);
    setSelectedUserCode(null);
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    console.log('KVKDashboard component mounted');
    fetchKVKDashboardData();
  }, []);

  return (
    <>
      {/* Dashboard Cards */}
      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card sales-card bg-gradient-danger card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Active User</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faUsers} /> </i>
              </div>
              <div className="ps-3">
                <h6 id="recordCount">{localLoading || loading ? 'Loading...' : userCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button 
                className='btn btn-link p-0 text-white' 
                onClick={handleUserMoreInfo}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xxl-6 col-xl-6">
        <div className="card info-card revenue-card bg-gradient-info card-img-holder">
          <div className="card-body">
            <img src={Circles} className="card-img-absolute" alt="circle-image" />
            <h5 className="card-title">Demonstration Machine</h5>
            <div className="d-flex align-items-center">
              <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                <i><FontAwesomeIcon icon={faTractor} /> </i>
              </div>
              <div className="ps-3">
                <h6>{localLoading || loading ? 'Loading...' : demonstrationCount}</h6>
              </div>
            </div>
            <div className="mt-3">
              <button 
                className='btn btn-link p-0 text-white' 
                onClick={handleDemonstrationMoreInfo}
                title="Click for more information"
              >
                <i className="bi bi-arrow-right-circle-fill fs-6">More Info</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Details Table */}
      {activeTable === 'user' && (
        <div className="col-lg-12" id="kvk-user-details-table">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title1">KVK Active Users Details</h5>
              <div className="d-flex justify-content-end mb-2">
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(userDetails, "KVK_Active_Users_Report", ["S.No", "User Name", "Count"])}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>User Name</th>
                      <th>Count</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails && userDetails.length > 0 ? (
                      userDetails.map((user, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{user.kvK_Name}</td>
                          <td>{user.totalUsers}</td>
                          <td>
                            <span
                              role="button"
                              onClick={() => toggleDrillDownData(user.kvK_Name, user.kvkCode)}
                              style={{
                                cursor: "pointer",
                                color: "#0d6efd",
                                fontSize: "16px",
                                fontWeight: "bold"
                              }}
                              title={showDrillDown && selectedUserCode === user.kvkCode ? "Close Details" : "View Details"}
                            >
                              {showDrillDown && selectedUserCode === user.kvkCode ? "-" : "+"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">No user data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drill-Down Table for User Details */}
      {showDrillDown && drillDownData.length > 0 && (
        <div className="col-lg-12" id="kvk-drilldown-table">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title1 mb-0">User Role Details - {drillDownData[0]?.kvK_Name}</h5>
                <button 
                  className="btn btn-sm btn-secondary" 
                  onClick={closeDrillDown}
                >
                  <i className="bi bi-x-circle"></i> Close
                </button>
              </div>
              <div className="d-flex justify-content-end mb-2">
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(drillDownData, `${drillDownData[0]?.userName}_User_Roles_Report`, ["S.No", "User Role Name", "Total Users"])}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>S.No</th>
                      <th>User Role Name</th>
                      <th>Total Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillDownData && drillDownData.length > 0 ? (
                      drillDownData.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.kvK_Name}</td>
                          <td>{item.totalUsers}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center">No data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demonstration Details Table */}
      {activeTable === 'demonstration' && (
        <div className="col-lg-12" id="kvk-demonstration-details-table">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title1">KVK Demonstration Details</h5>
              <div className="d-flex justify-content-end mb-2">
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => exportToCSV(demonstrationDetails, "KVK_Demonstration_Report", ["S.No", "Location", "Count"])}
                >
                  Export CSV
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>KVK Name</th>
                      <th>Count</th>
                      
                    </tr>
                  </thead>
                  <tbody>
                    {demonstrationDetails && demonstrationDetails.length > 0 ? (
                      demonstrationDetails.map((demo, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                         
                          <td>{demo.kvK_Name}</td>
                          <td>{demo.total}</td>
                          
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">No demonstration data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KVKDashboard;
